import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCardIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/composed/search-input";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import * as m from "@/paraglide/messages";
import { $adminListSubscriptions } from "@/server/actions/subscription";

import { SubscriptionRow } from "./-components/subscription-row";

export const Route = createFileRoute("/admin/subscriptions")({
	component: RouteComponent,
});

function getStatusLabels(): Record<string, string> {
	return {
		active: m.status_active(),
		trialing: m.status_trialing(),
		canceled: m.status_canceled(),
		incomplete: m.status_incomplete(),
		past_due: m.status_pastDue(),
		unpaid: m.status_unpaid(),
		paused: m.status_paused(),
	};
}

function RouteComponent() {
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 300);

	const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
		queryKey: [
			"subscriptions",
			"admin",
			{ pageSize: 20, search: debouncedSearch || undefined },
		],
		queryFn: async ({ pageParam }) => {
			return $adminListSubscriptions({
				data: {
					page: pageParam,
					pageSize: 20,
					search: debouncedSearch || undefined,
				},
			});
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			const totalPages = Math.ceil(lastPage.total / lastPage.pageSize);
			return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
		},
	});

	const items = data?.pages.flatMap((page) => page.data) ?? [];
	const statusLabels = getStatusLabels();

	return (
		<div className="container mx-auto py-6 px-2">
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<CreditCardIcon className="size-5" />
						{m.adminNav_subscriptions()}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<SearchInput
						wrapperClassName="max-w-sm"
						placeholder={m.adminSubs_searchPlaceholder()}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>

					{isLoading ? (
						<div className="py-8 text-center text-muted-foreground">
							{m.common_loading()}
						</div>
					) : items.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-10" />
									<TableHead>{m.adminSubs_colUser()}</TableHead>
									<TableHead>{m.adminSubs_colPlan()}</TableHead>
									<TableHead>{m.adminSubs_colStatus()}</TableHead>
									<TableHead>{m.adminSubs_colStartDate()}</TableHead>
									<TableHead>{m.adminSubs_colEndDate()}</TableHead>
									<TableHead className="w-[100px]">
										{m.adminSubs_colActions()}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{items.map((sub) => (
									<SubscriptionRow
										key={sub.id}
										sub={sub}
										statusLabels={statusLabels}
									/>
								))}
							</TableBody>
						</Table>
					) : (
						<Empty>
							<EmptyDescription>
								{debouncedSearch ? m.adminSubs_noMatch() : m.adminSubs_empty()}
							</EmptyDescription>
						</Empty>
					)}

					{hasNextPage && (
						<div className="flex justify-center pt-4">
							<Button
								variant="outline"
								onClick={() => fetchNextPage()}
								disabled={isFetchingNextPage}
							>
								{isFetchingNextPage ? m.common_loading() : m.common_loadMore()}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
